import {
  type ExperimentProduct,
  type PaywallApi,
  type PaymentPlatform,
  type UserInfo,
  fetchExperimentProducts,
  mapPlatformToServiceApi,
} from '@forest/paywall-core'
import { compare } from 'compare-versions'
import pRetry from 'p-retry'
import { native } from '../native'
import { canonicalMock } from './canonical-mock'
import {
  readExpSkusCache,
  readSubscriptionProductsCache,
  writeExpSkusCache,
  writeSubscriptionProductsCache,
} from './cache'
import {
  ANNUAL_DOUBLE_TRIAL,
  buildAnnualDoubleTrial,
} from './annual-double-trial'
import { getSubscriptionProductsBySkus, mergeProducts } from './merge'
import {
  MONTHLY_7DAYS_TRIAL,
  buildMonthly7daysTrial,
} from './monthly-7days-trial'
import {
  MONTHLY_NO_TRIAL,
  buildMonthlyNoTrial,
  type DeployEnv,
} from './monthly-no-trial'
import type { Product, Products, SubscriptionProduct } from './types'

export interface GetProductsOptions {
  cacheKeyPrefix: string
  api: PaywallApi
  appVersion: string
  platform: PaymentPlatform
  deferredUserInfo: Promise<UserInfo>
  env: DeployEnv
  shouldMock: boolean
}

export async function getProducts(
  options: GetProductsOptions,
): Promise<Products> {
  const {
    cacheKeyPrefix,
    api,
    appVersion,
    platform,
    deferredUserInfo,
    env,
    shouldMock,
  } = options

  if (shouldMock) return canonicalMock(env, platform)

  const supportsGetStoreRegion = compare(appVersion, '5.7.0', '>=')
  const supportsGetAvailableSubs = compare(appVersion, '5.2.0', '>=')

  // Cache A — subscription products (user-independent, only applicable on ≥5.2.0).
  const subsCacheKey = `${cacheKeyPrefix}.subs.${env}.${platform}`
  const cachedSubs = supportsGetAvailableSubs
    ? readSubscriptionProductsCache(subsCacheKey)
    : null

  // Fire native subscription fetch eagerly on miss; on hit, skip the native call entirely.
  const promiseSubscriptionProducts: Promise<SubscriptionProduct[]> | null =
    cachedSubs
      ? Promise.resolve(cachedSubs)
      : supportsGetAvailableSubs
        ? pRetry(async () => {
            const r = await native.iap.getAvailableSubscriptionProducts()
            if (!r.success) throw new Error(r.error)
            return r.data.products
          })
        : null

  // Store region — eager, cheap, rejection-suppressed so unused-on-cache-hit is safe.
  const promiseStoreRegion: Promise<string | null> = supportsGetStoreRegion
    ? native.iap
        .getStoreRegion()
        .then((r) => (r.success ? r.data.region : null))
        .catch(() => null)
    : Promise.resolve(null)

  // Need user info to check exp cache and to call fetchExperimentProducts on miss.
  const userInfo = await deferredUserInfo

  // Cache B — experiment SKUs (per-user).
  const expCacheKey = `${cacheKeyPrefix}.exp.${userInfo.user_id}`
  const cachedExp = readExpSkusCache(expCacheKey)

  const promiseExpSkus: Promise<ExperimentProduct[]> = cachedExp
    ? Promise.resolve(cachedExp)
    : promiseStoreRegion.then((storeRegion) =>
        fetchExperimentProducts(api.service, {
          userId: userInfo.user_id,
          platform: mapPlatformToServiceApi(platform),
          storeRegion,
        }),
      )

  // For <5.2.0, subscription products must be synthesized from exp skus via getPrice.
  const finalSubscriptionProducts: Promise<SubscriptionProduct[]> =
    promiseSubscriptionProducts ??
    pRetry(async () => {
      const expSkus = await promiseExpSkus
      return getSubscriptionProductsBySkus(expSkus, platform, native.iap)
    })

  const [expSkus, subscriptionProducts] = await Promise.all([
    promiseExpSkus,
    finalSubscriptionProducts,
  ])

  if (!cachedSubs && supportsGetAvailableSubs) {
    writeSubscriptionProductsCache(subsCacheKey, subscriptionProducts)
  }
  if (!cachedExp) writeExpSkusCache(expCacheKey, expSkus)

  const four = mergeProducts(expSkus, subscriptionProducts, platform)
  const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null
  const monthlyNoTrial: Product | null = noTrialConfig
    ? buildMonthlyNoTrial(noTrialConfig, subscriptionProducts)
    : null

  const doubleTrialConfig = ANNUAL_DOUBLE_TRIAL[env]?.[platform] ?? null
  const annualDoubleTrial: Product | null = doubleTrialConfig
    ? buildAnnualDoubleTrial(doubleTrialConfig, subscriptionProducts)
    : null

  const sevenDaysTrialConfig = MONTHLY_7DAYS_TRIAL[env]?.[platform] ?? null
  const monthly7daysTrial: Product | null = sevenDaysTrialConfig
    ? buildMonthly7daysTrial(sevenDaysTrialConfig, subscriptionProducts)
    : null

  return { ...four, monthlyNoTrial, annualDoubleTrial, monthly7daysTrial }
}
